import {
  getRelativePosition,
  useGetCollector,
  useUpdateCollector,
} from "@/api/collector/collector";
import { Collector } from "@/lib/types";
import { useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";

import { collectorSchema } from "@/api/collector/collector-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import { MaskableSeparator as Separator } from "@/components/form-items/MaskableSeparator";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { FormLayout } from "@/layout/FormLayout";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const schema = collectorSchema.passthrough();

const EditCollector = ({ collector }: { collector: Collector }) => {
  const { instanceData } = useAuth();

  const form = useForm({
    reValidateMode: "onSubmit",
    resolver: standardSchemaResolver(schema),
    defaultValues: {
      ...collector,
      collectionneursDescriptionVillePublished:
        collector.collectionneursDescriptionVillePublished ?? true,
      collectionneursDescriptionPaysPublished:
        collector.collectionneursDescriptionPaysPublished ?? true,
    },
  });

  const collectorUpdater = useUpdateCollector();

  const { relativePosition } = useDashboardParams<Collector>({
    statusField: "collectionneursPublicationStatus",
    resourceName: "bibliography",
    relativePosition: {
      currentId: collector.id,
      positionFetcher: getRelativePosition,
    },
  });

  const isModuleSculptureEnabled = useMemo(() => {
    return instanceData?.isModuleSculptureEnabled;
  }, [instanceData?.isModuleSculptureEnabled]);

  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const [, setSearchParam] = useSearchParams();

  return (
    <FormLayout
      formKey="collector"
      publicationStatus={collector.collectionneursPublicationStatus}
      onSave={(status) => {
        form.handleSubmit(
          (values) => {
            collectorUpdater.mutate(
              {
                ...values,
                ...(status && { collectionneursPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("collectorSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.collectionneursDescriptionNom != undefined)
              setSearchParam({ tab: "description" });
          },
        )();
      }}
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["collectors"],
        });
        queryClient.invalidateQueries({
          queryKey: ["collector", collector.id],
        });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: collector.id }],
        });
      }}
      relativePosition={relativePosition}
      title={`${collector.collectionneursDescriptionCivilite} ${collector.collectionneursDescriptionPrenom} ${collector.collectionneursDescriptionNom}`}
      reference={`${collector.collectionneursDescriptionVille ?? ""} ${collector.collectionneursDescriptionVille && collector.collectionneursDescriptionPays ? "-" : ""} ${collector.collectionneursDescriptionPays ?? ""}`}
      id={collector.id}
      form={form}
      type="collector"
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        formKey="collector"
        tabs={
          [
            { nonHideable: true,
              value: "description",
              label: t("collectorDescription"),
            },
            { value: "sources", label: t("sources") },
            { value: "links", label: t("linkedEntries") },
          ] as const
        }
      >
        <AF.Tab value="description">
          <AF.Col>
            <ModifiableList
              className="w-32"
              name="collectionneursDescriptionCivilite"
              listKey="liste-civilites"
            />
            <AF.Text name="collectionneursDescriptionPrenom" />
            <AF.Text name="collectionneursDescriptionNom" nonMaskable />
            <AF.Text name="collectionneursDescriptionNomTri" />
            <ModifiableList
              name="collectionneursDescriptionNomPublic"
              listKey="liste-collections-catalogue"
            />
            <AF.Text name="collectionneursDescriptionLieu" />
            <AF.Text name="collectionneursDescriptionRue" />
            <AF.Text name="collectionneursDescriptionCodePostal" />
            <ModifiableList
              name="collectionneursDescriptionVille"
              listKey="liste-villes"
              corner={() => (
                <AF.Switch
                  name="collectionneursDescriptionVillePublished"
                  labelFirst
                />
              )}
            />
            <ModifiableList
              name="collectionneursDescriptionPays"
              listKey="liste-pays"
              corner={() => (
                <AF.Switch
                  name="collectionneursDescriptionPaysPublished"
                  labelFirst
                />
              )}
            />
            <AF.Checkbox
              name="collectionneursDescriptionCollectionPublique"
              align={true}
            />
          </AF.Col>
          <AF.Col>
            <AF.Text name="collectionneursDescriptionNomContact" />
            <AF.Row>
              <AF.Text name="collectionneursDescriptionTelephoneMobile" />
              <AF.Text name="collectionneursDescriptionTelephone" />
            </AF.Row>
            <AF.Text name="collectionneursDescriptionAdresseMail" />
            <AF.Text name="collectionneursDescriptionSiteWeb" />
            <AF.RichText name="collectionneursDescriptionNotes" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            type="collector"
            id={collector.id}
            data={collector}
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedSculptureEditions",
                      label: t("entrysculptureeditions"),
                      type: "link-simple",
                      hidden: !isModuleSculptureEnabled,
                    },
                    {
                      name: "relatedTirage",
                      label: t("entrytirages"),
                      type: "link-simple",
                    },
                    {
                      name: "collectorInMultipleprints",
                      label: t("entrymultipleprints"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "estampesDansCollectionneursDescriptionInfos",
                          type: "text",
                          label: t(
                            "estampesDansCollectionneursDescriptionInfos",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedMultiplesample",
                      label: t("entrymultiplesamples"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditCollectorPage = () => {
  const { id } = useParams();
  const { data, isLoading } = useGetCollector(id);
  const { t } = useTranslation();
  return (
    <>
      {data && <EditCollector key={data.id} collector={data} />}
      {!data && !isLoading && (
        <h2 className="p-4">{t("collectionneurNExistePasLabel")}</h2>
      )}
    </>
  );
};
